import * as XLSX from 'xlsx';
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(value);
};

// Format currency with color: green (+) for positive, red (-) for negative
export const formatCurrencyColored = (value) => {
  if (value === null || value === undefined || value === 0) return { text: '$0', color: 'text-gray-400', isPositive: null };
  
  const absValue = Math.abs(value);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(absValue);
  
  if (value > 0) {
    return { text: `+${formatted}`, color: 'text-success', isPositive: true };
  } else {
    return { text: `-${formatted}`, color: 'text-red-500', isPositive: false };
  }
};

// Component helper for colored currency
export const ColoredAmount = ({ value, className = '' }) => {
  const { text, color } = formatCurrencyColored(value);
  return { text, color, className: `${color} font-mono ${className}` };
};

export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export const formatDateTime = (dateString, timeString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const formatted = date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  return timeString ? `${formatted} ${timeString}` : formatted;
};

export const getMonthName = (monthStr) => {
  if (!monthStr || typeof monthStr !== 'string') return '';
  const months = {
    '01': 'Gennaio', '02': 'Febbraio', '03': 'Marzo',
    '04': 'Aprile', '05': 'Maggio', '06': 'Giugno',
    '07': 'Luglio', '08': 'Agosto', '09': 'Settembre',
    '10': 'Ottobre', '11': 'Novembre', '12': 'Dicembre'
  };
  const parts = monthStr.split('-');
  if (parts.length < 2) return monthStr;
  const [year, month] = parts;
  return `${months[month] || month} ${year}`;
};

export const getTodayString = () => {
  return new Date().toISOString().split('T')[0];
};

export const getCurrentMonth = () => {
  return new Date().toISOString().slice(0, 7);
};

export const exportToExcel = (data, columns, filename) => {
  const worksheet = XLSX.utils.json_to_sheet(
    data.map(row => {
      const obj = {};
      columns.forEach(col => {
        obj[col.header] = col.accessor ? 
          (typeof col.accessor === 'function' ? col.accessor(row) : row[col.accessor]) 
          : row[col.key];
      });
      return obj;
    })
  );
  
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Dati');
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

export const groupByMonth = (data, dateField = 'date') => {
  return data.reduce((acc, item) => {
    const month = item[dateField]?.slice(0, 7) || 'unknown';
    if (!acc[month]) acc[month] = [];
    acc[month].push(item);
    return acc;
  }, {});
};

export const groupByYear = (data, dateField = 'date') => {
  return data.reduce((acc, item) => {
    const year = item[dateField]?.slice(0, 4) || 'unknown';
    if (!acc[year]) acc[year] = [];
    acc[year].push(item);
    return acc;
  }, {});
};

export const sumBy = (arr, field) => {
  return arr.reduce((sum, item) => sum + (item[field] || 0), 0);
};
